
import { formatDateNumber } from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { UrgentDeliveryManagementController } from "@/app/ui/controllers/UrgentDeliveryManagementController";
import { ApiStateData } from "@/domain/entities/Api";
import { ErrorMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import {
  LastLocationOption,
  UrgentDeliveryManagementDetailData,
  UrgentDeliveryManagementRTSData,
  UrgentDeliveryManagementRTSListRequest,
  UrgentDeliveryManagementRTSListRequestDownload
} from "@/domain/entities/UrgentDeliveryManagement";
import { Options, Vue } from "vue-class-component";
import Detail from "../detail.vue";
import UrgentRTSFilter from "./filter.vue";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";

@Options({
  components: {
    Detail,
    UrgentRTSFilter
  }
})
export default class UrgentRTS extends Vue {
  controller = UrgentDeliveryManagementController;
  parameters = {
    search: "",
    caseType: new OptionsClass(),
    productType: new OptionsClass(),
    sttClientAggregatorType: new OptionsClass(),
    cityCode: new OptionsClass(),
    hubId: new OptionsClass(),
    startDate: new Date(
      new Date(new Date().setMonth(new Date().getMonth() - 1))
    ) as Date | null,
    endDate: new Date() as Date | null,
    sortBy: "",
    sortDirection: ""
  };

  modalDetail = false;
  apiDetailData = new ApiStateData();
  urgentDeliveryManagementDetailData = new UrgentDeliveryManagementDetailData();
  dateRangeValue: { start: string; end: string } = {
    start: "",
    end: ""
  };

  get lastLocationOptions(): LastLocationOption[] {
    return this.controller.lastLocationOptions || [];
  }

  get columns() {
    return [
      {
        name: this.$t("urgentDeliveryManagement.rts.columnNo"),
        styleHead: "text-left head-no",
        render: (item: UrgentDeliveryManagementRTSData, i: number) =>
          `<span class="${
            this.getStyleByFlag(item.pdFlag).class
          } font-normal text-black-lp-300 whitespace-nowrap">${i +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</span>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnNoStt"),
        styleHead: "text-left head-no-stt",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="flex flex-col gap-1 text-sm text-black-lp-100">
          <div>
            ${item.pdSTTNo}
          </div>
          ${
            item.pdFlag
              ? `<div class="flex flex-row gap-1">
              <img src="${require(`@/app/ui/assets/svg/${
                this.getStyleByFlag(item.pdFlag).icon
              }`)}" class="text-red-lp-100" width="16" />
              <div class="font-medium ${
                this.getStyleByFlag(item.pdFlag).textClass
              }">${item.pdFlag}</div>
            </div>`
              : ""
          }
        </div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnCaseType"),
        styleHead: "text-left head-case-type",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm badge">${item.pdCaseType}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnReason"),
        styleHead: "text-left head-reason",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.pdReason}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnSttType"),
        styleHead: "text-left head-aggregator-type",
        render: (item: UrgentDeliveryManagementRTSData) => {
          const isAggregator =
            item.sttClientAggregatorType?.toLowerCase() === "aggregator";
          return `<div class="font-normal text-sm ${
            isAggregator ? "badge-red" : "badge-gray"
          } inline-block">${
            isAggregator
              ? this.$t("urgentDeliveryManagement.rts.aggregator")
              : this.$t("urgentDeliveryManagement.rts.nonAggregator")
          }</div>`;
        }
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnOriginCity"),
        styleHead: "text-left head-origin",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.sttOriginCityId} - ${item.sttOriginCityName}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnLastStatus"),
        styleHead: "text-left head-last-status",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.sttLastStatusId}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnLastLocation"),
        styleHead: "w-44 text-left",
        render: (item: UrgentDeliveryManagementRTSData) => {
          const cityCode = item.cityCode?.trim();
          const hubName = item.hubName?.trim();
          const stationBucket = item.stationBucket?.trim();

          let line1 = "-";
          if (cityCode && hubName) {
            line1 = `${cityCode} - ${hubName}`;
          } else if (cityCode) {
            line1 = cityCode;
          }

          const line2 = `Station : ${stationBucket || "-"}`;

          return `
            <div data-testid="urgent-rts-last-location-cell-${item.pdId ||
              "row"}" class="flex flex-col text-sm leading-tight text-black-lp-100">
              <span class="font-normal">${line1}</span>
              <span class="text-xs text-gray-lp-800">${line2}</span>
            </div>
          `.trim();
        }
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnProductType"),
        styleHead: "text-left head-origin",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.pdProductType ||
            "-"}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnDexAttempt"),
        styleHead: "text-left head-origin",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.pdDexCustomerAttempt}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnRequestDate"),
        styleHead: "text-left head-flagged-date",
        sortBy: "pd_created_at",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.formatFlaggedDate}</div> <span class="text-xs text-gray-lp-800">${item.createdBy}</span>`
      },
      {
        name: this.$t("urgentDeliveryManagement.rts.columnBkdDate"),
        styleHead: "text-left head-bkd-date",
        sortBy: "stt_booked_at",
        render: (item: UrgentDeliveryManagementRTSData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.formatBkdDate}</div> <span class="text-xs text-gray-lp-800">${item.pdBkdByName}</span>`
      }
    ];
  }

  onApplyFilter(params: any) {
    this.parameters.caseType = params.caseType;
    this.parameters.productType = params.productType;
    this.parameters.sttClientAggregatorType = params.sttClientAggregatorType;

    const cityCodesStr = (params.cityCodes || []).join(",");
    const hubIdsStr = (params.hubIds || []).map(String).join(",");

    this.parameters.cityCode = new OptionsClass({ value: cityCodesStr });
    this.parameters.hubId = new OptionsClass({ value: hubIdsStr });

    this.fetchList(true);
  }

  mounted(): void {
    this.controller.getLastLocationOptions();
    this.fetchList(true);
  }

  fetchList(reset = false): void {
    if (reset) {
      this.controller.paginationData.page = 1;
    }

    this.controller.getRTSList(
      new UrgentDeliveryManagementRTSListRequest({
        sttNo: this.parameters.search,
        caseType: this.parameters.caseType.value,
        page: this.controller.paginationData.page,
        limit: this.controller.paginationData.limit,
        productType: this.parameters.productType.value,
        sttClientAggregatorType: this.parameters.sttClientAggregatorType.value,
        cityCode: this.parameters.cityCode.value,
        hubId: this.parameters.hubId.value,
        startDate: this.parameters.startDate
          ? formatDateNumber(this.parameters.startDate)
          : "",
        endDate: this.parameters.endDate
          ? formatDateNumber(this.parameters.endDate)
          : "",
        sortBy: this.parameters.sortDirection,
        sortColumn: this.parameters.sortBy
      })
    );
  }

  onSearch(value: string) {
    if (!value.length || value.length >= 3) {
      this.parameters.search = value;
      this.fetchList(true);
    }
  }

  clearSearch() {
    this.parameters.search = "";
    this.fetchList(true);
  }

  getStyleByFlag(
    flag: string
  ): {
    class: string;
    icon: string;
    textClass: string;
  } {
    if (flag.toLowerCase().includes("hari ini")) {
      return {
        class: "",
        icon: "clock-normal-red.svg",
        textClass: "text-red-lp-100"
      };
    }
    if (flag.toLowerCase().includes("lewat")) {
      return {
        class: "",
        icon: "clock-exclamation.svg",
        textClass: "text-yellow-lp-100"
      };
    } else {
      return {
        class: "normal",
        icon: "clock-normal.svg",
        textClass: "text-gray-lp-500"
      };
    }
  }

  get emptyStateData(): {
    headerMessage: string;
    message: string;
    icon: string;
  } {
    if (this.parameters.search.length) {
      return {
        headerMessage: this.$t(
          "urgentDeliveryManagement.empty.filterNotFound"
        ) as string,
        message: this.$t(
          "urgentDeliveryManagement.empty.filterNotFoundMessage"
        ) as string,
        icon: "noData_filter"
      };
    } else {
      return {
        headerMessage: this.$t(
          "urgentDeliveryManagement.empty.header"
        ) as string,
        message: this.$t("urgentDeliveryManagement.empty.message") as string,
        icon: "noData_empty"
      };
    }
  }

  get pagination() {
    return this.controller.paginationData;
  }

  async onDownload() {
    await this.controller.downloadUrgentRTSList(
      new UrgentDeliveryManagementRTSListRequestDownload({
        sttNo: this.parameters.search,
        caseType: this.parameters.caseType.value,
        productType: this.parameters.productType.value,
        sttClientAggregatorType: this.parameters.sttClientAggregatorType.value,
        cityCode: this.parameters.cityCode.value,
        hubId: this.parameters.hubId.value,
        sortColumn: this.parameters.sortBy,
        sortBy: this.parameters.sortDirection,
        startDate: this.parameters.startDate
          ? formatDateNumber(this.parameters.startDate)
          : "",
        endDate: this.parameters.endDate
          ? formatDateNumber(this.parameters.endDate)
          : ""
      })
    );
  }

  async goToDetail(item: UrgentDeliveryManagementRTSData) {
    await this.getDetailData(item.pdId);
  }

  closeDetail() {
    this.modalDetail = false;
  }

  async getDetailData(id: number) {
    MainAppController.showLoading();
    try {
      this.urgentDeliveryManagementDetailData = await UrgentDeliveryManagementController.getPriorityDeliveryManagementDetail(
        id
      );
      this.modalDetail = true;
      this.apiDetailData.errorType = "";
    } catch (error) {
      MainAppController.closeLoading();
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          type: "client-error",
          title: this.$t(
            "urgentDeliveryManagement.error.getDetailFailed"
          ) as string,
          onClose: () => {
            MainAppController.closeErrorMessage();
          }
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onSetDateRange(value: Array<any>): void {
    const startDate = value[0];
    const endDate = value[1];

    this.parameters.startDate = startDate;
    this.parameters.endDate = endDate;

    if (startDate && endDate) this.fetchList(true);
  }

  onSort(sort: { sortBy: string; sortDirection: "ASC" | "DESC" }) {
    this.parameters.sortBy = sort.sortBy;
    this.parameters.sortDirection = sort.sortDirection;
    this.fetchList(true);
  }
}
